/**
 * Button component <uo-button>
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
// Import variables
import './variables.scss';
// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
// Import global utils
import { isExternalUrl } from '@automator/shared/utils/is-external-url';
import { isEmpty } from '@automator/shared/utils/is-empty';
import { confirm } from '@automator/shared/utils/confirm';
// Import style for web component
import styles from './web-component-style.scss?inline';
/**
 * @element uo-button
 * @summary A versatile button component supporting links, form submission, loading/disabled states, and confirmation dialogs.
 *
 * @slot - The default slot contains the button's label/content (text, icons, etc.).
 *
 * @attr {boolean} [disabled=false] - Disables the button, preventing interaction. Reflects `disabled` property.
 * @attr {boolean} [loading=false] - Shows a loading indicator, also disables the button. Reflects `loading` property.
 * @attr {'secondary' | 'danger' | 'success' | 'transparent'} [color] - Sets the color scheme of the button. Maps to `color` property.
 * @attr {boolean} [filled=false] - Applies a filled style variant. Reflects `filled` property.
 * @attr {'small' | 'extra-small'} [size] - Sets the size variant of the button. Maps to `size` property.
 * @attr {string} [href] - If set, the button acts as a hyperlink. Maps to `href` property.
 * @attr {'_self' | '_blank' | '_parent' | '_top' | string} [target='_self'] - Specifies the target for the link (if `href` is set). Maps to `target` property.
 * @attr {'submit'} [type] - If set to 'submit', the button triggers form submission. Maps to `type` property.
 * @attr {string} [name] - The name associated with the button, used during form submission. Maps to `name` property.
 * @attr {string} [value] - The value associated with the button, used during form submission. Maps to `value` property.
 * @attr {boolean} [unsafe-force-target=false] - If true, forces the specified `target` even for external links that would normally default to `_blank`. Maps to `unsafeForceTarget` property.
 * @attr {boolean} [needs-confirmation=false] - If true, displays a confirmation dialog before proceeding with the link or form submission. Maps to `needsConfirmation` property.
 * @attr {string} [confirmation-heading] - Heading text for the confirmation dialog. Maps to `confirmationHeading` property.
 * @attr {string} [confirmation-content] - Content text for the confirmation dialog. Maps to `confirmationContent` property.
 * @attr {string} [confirmation-button-label] - Label for the confirm button in the dialog. Maps to `confirmationButtonLabel` property.
 *
 * @cssprop {string | auto} [--uap-button-host-style-width=auto] - Controls the width of the host element (`:host`).
 * @cssprop {string} [--uap-button-style-custom-background=var(--uap-color-primary)] - Overrides the default background color, primarily for the `filled` state or specific color variants. Default depends on the variant (e.g., `#fff` for outline, `var(--uap-color-primary)` for default filled).
 * @cssprop {string} [--uap-button-style-border-radius=var(--uap-border-radius)] - Controls the button's border-radius.
 * @cssprop {string} [--uap-button-style-custom-font-family=var(--uap-font-family)] - Overrides the button's font family.
 * @cssprop {string | number} [--uap-button-style-custom-font-weight=500] - Overrides the button's font weight.
 * @cssprop {string} [--uap-button-style-box-shadow=var(--uap-box-shadow-small)] - Sets the default box-shadow for the button (non-focus state).
 * @cssprop {string} [--uap-button-style-custom-text-align=start] - Overrides the text alignment within the button.
 * @cssprop {string} [--uap-button-style-display=inline-block] - Controls the `display` property of the internal button/link element. Set to `inline-flex` or `flex` to use `align-items`.
 * @cssprop {string} [--uap-button-style-align-items=normal] - Controls vertical alignment when `display` is `flex` or `inline-flex`.
 * @cssprop {string} [--uap-button-style-custom-color=var(--uap-color-primary)] - Overrides the default text color. Default depends on the variant (e.g., `var(--uap-color-primary)` for default outline, `#fff` for default filled).
 * @cssprop {string} [--uap-button-style-custom-border-color=var(--uap-color-primary)] - Overrides the default border color (primarily for outline styles). Default depends on the variant.
 * @cssprop {string} [--uap-button-style-custom-hover-background=var(--uap-color-primary)] - Overrides the background color for `hover`, `focus`, `disabled`, and `loading` states. Default depends on the variant.
 * @cssprop {string} [--uap-button-style-custom-hover-color=#fff] - Overrides the text color for `hover`, `focus`, `disabled`, and `loading` states. Default depends on the variant.
 * @cssprop {string} [--uap-button-style-custom-hover-border-color] - Overrides the border color for `hover`, `focus`, `disabled`, and `loading` states (used by secondary, danger, success variants). Default depends on the variant.
 * @cssprop {string} [--uap-button-style-focus-box-shadow=0 0 0 0.2rem] - Defines the spread and blur of the focus ring box-shadow (the color part is separate).
 * @cssprop {string} [--uap-button-style-custom-shadow=var(--uap-color-primary)] - Defines the HEX color value (e.g., `#0790e8`) used for the focus ring shadow. Default depends on the variant.
 * @cssprop {string} [--uap-button-style-font-size=var(--uap-font-size--body-small)] - Controls the base font size inside the button (can be overridden by size variants).
 * @cssprop {string | number} [--uap-button-style-line-height=var(--uap-line-height--body-small)] - Controls the base line height inside the button (can be overridden by size variants).
 * @cssprop {string} [--uap-button-style-height=34px] - Controls the explicit height of the button (can be overridden by size variants).
 * @cssprop {string} [--uap-button-style-padding-top=0px] - Controls the top padding inside the button.
 * @cssprop {string} [--uap-button-style-padding-bottom=0px] - Controls the bottom padding inside the button.
 * @cssprop {string} [--uap-button-style-padding-left=15px] - Controls the left padding inside the button (can be overridden by size variants).
 * @cssprop {string} [--uap-button-style-padding-right=15px] - Controls the right padding inside the button (can be overridden by size variants).
 */
let AutomatorButton = class AutomatorButton extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Renders a filled button style variant. Reflects to attribute.
         * @attr filled
         * @default false
         */
        this.filled = false;
        /**
         * Target attribute for the navigation (_self, _blank, etc.). Only applies when `href` is set.
         * @attr target
         * @default '_self'
         */
        this.target = '_self';
        /**
         * Disables the button, preventing user interaction. Reflects to attribute.
         * @attr disabled
         * @default false
         */
        this.disabled = false;
        /**
         * Shows a loading indicator and disables the button. Reflects to attribute.
         * @attr loading
         * @default false
         */
        this.loading = false;
        /**
         * If true, forces the use of the specified `target`, overriding default behavior for external links (which open in `_blank`).
         * @attr unsafe-force-target
         * @default false
         */
        this.unsafeForceTarget = false;
        /**
         * If true, requires user confirmation via a dialog before navigating (if `href` is set) or submitting a form (if `type` is 'submit').
         * @attr needs-confirmation
         * @default false
         */
        this.needsConfirmation = false;
    }
    /**
     * Renders the component's DOM structure based on its properties and slotted content.
     * @return {TemplateResult} The HTML template for the button component.
     */
    render() {
        // Some notes:
        // - tabindex="0" ensures it's focusable.
        return html `
			<button
				size=${ifDefined(this.size)}
				color=${ifDefined(this.color)}
				type=${ifDefined(this.type)}

				?disabled=${this.disabled || this.loading}
				?loading=${this.loading}
				?filled=${this.filled}

				tabindex="0"

				@click=${this._handleClick}
			>
				<slot></slot> ${this._renderSuffixIcon()}
			</button>
		`;
    }
    /**
     * Renders the suffix icon (e.g., external link icon) if needed.
     * @return {TemplateResult | undefined} The icon template or undefined.
     * @protected
     */
    _renderSuffixIcon() {
        // Check if the link will open in a new tab based on calculated target
        if (this._calculateTarget() === '_blank') {
            return html `<uo-icon id="external-link" icon-style="solid"></uo-icon>`;
        }
    }
    /**
     * Handles click events on the button host.
     * @param {PointerEvent} event - The click event.
     * @protected
     */
    async _handleClick(event) {
        // If the button is disabled or loading, prevent the default behavior
        if (this.disabled || this.loading) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        // Store the composed path early so we can use it in the form submission handler.
        // This is necessary because the composed path is not available after the await confirm() call.
        const composedPath = event.composedPath();
        /**
      * 1. Confirmation step
      */
        // Ask the user to confirm the action first
        if (this.needsConfirmation) {
            try {
                await confirm({
                    heading: this.confirmationHeading,
                    content: this.confirmationContent,
                    confirmButtonLabel: this.confirmationButtonLabel
                });
            }
            catch (error) { // eslint-disable-line no-unused-vars
                // User cancelled confirmation
                event.preventDefault();
                event.stopPropagation();
                // Stop here
                return;
            }
        }
        /**
      * 2. Navigation step
      */
        // Determine if the user intends to open in a new tab (middle click, ctrl/cmd click)
        const isNewTabIntent = event.button === 1 || event.metaKey || event.ctrlKey;
        // Check if it has a hyperlink defined
        if (!isEmpty(this.href)) {
            // Open the link
            this._handleNavigation(isNewTabIntent);
        }
        else if (this.type === 'submit') {
            // If it's a submit button, let the default behavior happen unless confirmation was needed (handled above). If specific form association or value passing is needed without default submission, handle it here.
            // The current code simulates submission, which might be desired if the button isn't directly inside the form or needs name/value injection.
            this._handleFormSubmit(composedPath);
        }
    }
    /**
     * Handles the navigation logic when the button has an `href`.
     * @param {boolean} userRequestedNewTab - Whether the user interaction indicated a new tab request.
     * @protected
     */
    _handleNavigation(userRequestedNewTab) {
        // If there is no href, do nothing
        if (isEmpty(this.href)) {
            return;
        }
        // We can assume the href is a non-empty string
        // Calculate the target
        const target = this._calculateTarget(userRequestedNewTab);
        // Create an anchor element
        const anchor = document.createElement('a');
        anchor.href = this.href;
        anchor.target = target;
        // Add rel="noopener noreferrer" for security when opening in new tabs
        if (target === '_blank') {
            anchor.rel = 'noopener noreferrer';
        }
        // Simulate a click on the anchor element
        anchor.click();
    }
    /**
     * Calculates the appropriate target attribute based on href, properties, and user interaction.
     * @param {boolean} [userRequestedNewTab=false] - Whether user interaction indicated a new tab.
     * @return {ButtonTarget} The calculated target value.
     * @protected
     */
    _calculateTarget(userRequestedNewTab = false) {
        // If there is no href, set the target to _self
        if (isEmpty(this.href)) {
            return '_self';
        }
        // Before returning the target, check if the user is trying to force the target to a new tab
        if (userRequestedNewTab) {
            // Open on a new tab regardless of the target. The user request is more important than the developer's forced target.
            return '_blank';
        }
        // Developer explicitly forces the target
        if (this.unsafeForceTarget) {
            // Return the target
            return this.target || '_self';
        }
        // Check if it's an external link
        if (isExternalUrl(this.href)) {
            // Only return _blank if the developer hasn't set a *different* specific target
            return (this.target && this.target !== '_self') ? this.target : '_blank';
        }
        // Internal links use the specified target or default to _self
        return this.target || '_self';
    }
    /**
     * Handles form submission logic when `type` is 'submit'.
     * Creates a temporary button to carry name/value if provided.
     * @param composedPath
     * @protected
     */
    _handleFormSubmit(composedPath) {
        // Get the closest form
        const $form = composedPath.find(el => el instanceof HTMLFormElement);
        // If there is no form, stop here
        if (isEmpty($form)) {
            console.warn('uo-button type="submit" was clicked, but no parent form was found.', this);
            return;
        }
        // Create a fake submit button
        const $submitButton = document.createElement('button');
        $submitButton.type = 'submit';
        // Check if it has a name and value defined
        if (!isEmpty(this.name)) {
            $submitButton.name = this.name;
        }
        if (!isEmpty(this.value)) {
            $submitButton.value = this.value;
        }
        // Add it to the form
        $form.appendChild($submitButton);
        // Trigger a click
        $submitButton.click();
        // Remove the button
        $form.removeChild($submitButton);
    }
};
/**
 * Defines the component's CSS styles.
 */
AutomatorButton.styles = css `${unsafeCSS(styles)}`;
/**
 * Configures the shadow root to delegate focus, allowing elements inside
 * the shadow DOM to receive focus when the host element is focused programmatically.
 */
AutomatorButton.shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true
};
__decorate([
    property({ type: String })
], AutomatorButton.prototype, "color", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], AutomatorButton.prototype, "filled", void 0);
__decorate([
    property({ type: String })
], AutomatorButton.prototype, "size", void 0);
__decorate([
    property({ type: String })
], AutomatorButton.prototype, "href", void 0);
__decorate([
    property({ type: String })
], AutomatorButton.prototype, "target", void 0);
__decorate([
    property({ type: String })
], AutomatorButton.prototype, "type", void 0);
__decorate([
    property({ type: String })
], AutomatorButton.prototype, "name", void 0);
__decorate([
    property({ type: String })
], AutomatorButton.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], AutomatorButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], AutomatorButton.prototype, "loading", void 0);
__decorate([
    property({ type: Boolean, attribute: 'unsafe-force-target' })
], AutomatorButton.prototype, "unsafeForceTarget", void 0);
__decorate([
    property({ type: Boolean, attribute: 'needs-confirmation' })
], AutomatorButton.prototype, "needsConfirmation", void 0);
__decorate([
    property({ type: String, attribute: 'confirmation-heading' })
], AutomatorButton.prototype, "confirmationHeading", void 0);
__decorate([
    property({ type: String, attribute: 'confirmation-content' })
], AutomatorButton.prototype, "confirmationContent", void 0);
__decorate([
    property({ type: String, attribute: 'confirmation-button-label' })
], AutomatorButton.prototype, "confirmationButtonLabel", void 0);
AutomatorButton = __decorate([
    customElement('uo-button')
], AutomatorButton);
export { AutomatorButton };
